import { Button, Modal, notification, Steps } from 'antd'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { parseEther } from 'viem'
import {
  useChainId,
  useWriteContract,
  useWaitForTransactionReceipt,
} from 'wagmi'

import attention from '@/assets/attention.png'
import BookDark from '@/assets/books-dark.png'
import BookLight from '@/assets/books-light.png'
import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import { config } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type { BridgeGroupConfigType } from '@/types/storeman-type'
import type { OpenStoremanStakeInTransParamsInterface } from '@/types/trans-params'
import { hexCharCodeToStr, wanPubKey2Address } from '@/utils'
import { useIwan } from '@/utils/useWebSocket'

import { Link } from '../stroeman-detail.module'

import StepFour from './step-four'
import StepOne from './step-one'
import StepThree from './step-three'
import StepTwo_1 from './step-two_1'
import StepTwo_2 from './step-two_2'

const initTransParam: OpenStoremanStakeInTransParamsInterface = {
  crosschain: '',
  groupId: '',
  PK: '',
  enodeID: '0',
  amount: '0',
}

export default function SetupBridge() {
  const chainId = useChainId()
  const [showInfoModal, setShowInfoModal] = useState(true)
  const [step, setStep] = useState(0)
  const { theme } = useLocalStorage()
  const isdark = useMemo(() => theme === 'dark', [theme])
  const [tarnsParam, setTransParam] = useState(initTransParam)
  const [nextBtnLoading, setNextBtnLoading] = useState(false)
  const { data: hash, writeContractAsync, isPending } = useWriteContract()
  const StoremanContractConfig = config[chainId]['StoremanContract']
  const { data: OpenStoremanGroupList, isSuccess } = useIwan<
    BridgeGroupConfigType[]
  >(['getStoremanGroupList'])
  const { isLoading: txReceiptIsLoading } = useWaitForTransactionReceipt({
    hash,
    query: {
      enabled: !!hash,
    },
  })

  const bridgeModalCln = clsx('bridgeModal', {
    'bridge-modal-dark': isdark,
    'bridge-modal-light': !isdark,
  })

  const OpenStoremanGroupRegisterList = useMemo(() => {
    if (
      isSuccess &&
      OpenStoremanGroupList &&
      OpenStoremanGroupList.length > 0
    ) {
      return OpenStoremanGroupList.sort(
        (a: BridgeGroupConfigType, b: BridgeGroupConfigType) =>
          Number(b.startTime) - Number(a.startTime),
      ).filter((item: BridgeGroupConfigType) => {
        if (process.env.REACT_APP_CHAINID === '888') {
          return item.canStakeIn
        } else {
          const groupName = hexCharCodeToStr(item.groupId)
          return item.canStakeIn && groupName.startsWith('testnet')
        }
      })
    } else {
      return []
    }
  }, [OpenStoremanGroupList])

  const openStoremanStakeIn = async () => {
    setNextBtnLoading(true)
    try {
      const hash = await writeContractAsync({
        address: StoremanContractConfig.addr,
        abi: StoremanContractConfig.abi,
        functionName: 'stakeIn',
        args: [tarnsParam.groupId, tarnsParam.PK, tarnsParam.enodeID],
        value: parseEther(tarnsParam.amount),
      })
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      setStep(2)
      setNextBtnLoading(false)
    } catch (error) {
      console.error('OpenStoremanStakeRegister', error)
      setNextBtnLoading(false)
      notification.error({
        message: 'Transaction Sent Failed',
        description: <Link txHash={hash || ''} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={error_icon} alt="notification" />,
      })
    }
  }

  const canRegist = useMemo(() => {
    return OpenStoremanGroupRegisterList.length !== 0
  }, [OpenStoremanGroupRegisterList])

  const onChange = (currentStep: number) => {
    if (currentStep === 1) {
      setStep(11)
    } else {
      setStep(currentStep)
    }
  }

  return (
    <div className="flex justify-center p-[1rem] mt-[30px]">
      <div className="flex gap-x-[3rem] justify-center md:flex-col-reverse md:w-full">
        <div className="min-w-[40%] bg-white rounded-2xl h-fit max-w-[1000px] min-h-[300px] p-[3rem] dark:bg-dark-222 md:min-h-fit">
          {step === 0 && <StepOne setStep={setStep} canRegist={canRegist} />}
          {step === 11 && (
            <StepTwo_1
              canRegist={canRegist}
              setStep={setStep}
              tarnsParam={tarnsParam}
              setTransParam={setTransParam}
              OpenStoremanGroupRegister={OpenStoremanGroupRegisterList[0]}
            />
          )}
          {step === 12 && (
            <StepTwo_2
              isPending={nextBtnLoading && (isPending || txReceiptIsLoading)}
              setStep={setStep}
              tarnsParam={tarnsParam}
              handleSendTrans={openStoremanStakeIn}
            />
          )}

          {step === 2 && (
            <StepThree
              canRegist={canRegist}
              setStep={setStep}
              wkAddr={
                tarnsParam.PK ? wanPubKey2Address(tarnsParam.PK, 'osm') : 'N/A'
              }
            />
          )}
          {step === 3 && <StepFour />}
        </div>
        <div className="flex flex-col md:mb-[20px] md:gap-4">
          <Steps
            className={clsx('min-h-[300px] md:min-h-fit', {
              'osm-steps-light': !isdark,
              'osm-steps-dark': isdark,
            })}
            direction={isMobile ? 'horizontal' : 'vertical'}
            size="small"
            responsive={false}
            onChange={onChange}
            current={[11, 12].includes(step) ? 1 : step}
            items={[
              { title: !isMobile && 'Overview' },
              {
                title: !isMobile && 'Register',
              },
              {
                title: !isMobile && 'Set Up',
              },
              {
                title: !isMobile && 'Monitor',
              },
            ]}
          />
          <div
            className="bg-white rounded-xl px-4 py-2 cursor-pointer flex gap-2 dark:bg-dark-222 relative top-[-3rem] md:sticky"
            onClick={() =>
              window.open(
                'https://docs.wanchain.org/wanchain-bridge-nodes/deploy-a-bridge-node/deploy-a-bridge-node-via-xstake-recommended',
                '_blank',
              )
            }
          >
            <img
              src={isdark ? BookDark : BookLight}
              alt="setup-bridge"
              className="w-[20px]"
            />
            <span className="dark:text-white">Setup Guide</span>
          </div>
        </div>
      </div>
      {OpenStoremanGroupRegisterList.length === 0 && isSuccess && (
        <Modal
          title="Notice"
          open={showInfoModal}
          centered
          wrapClassName={bridgeModalCln}
          footer={null}
          closable={false}
        >
          <img
            src={attention}
            width={120}
            alt="attention"
            className="mx-auto mb-4"
          />
          <div className="dark:text-white">
            Sorry, it’s currently outside the time window for creating a
            Storeman node, so the node cannot be created.
          </div>
          <div className="flex justify-between mt-[20px]">
            <Button
              onClick={() => setShowInfoModal(false)}
              className="text-white w-full rounded-2xl h-[60px] text-[18px] bg-light-111 border-none"
            >
              Got it
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
